import { useState } from 'react';
import { showSuccessMessage } from 'helpers/success';
import { showErrorMessage } from 'helpers/errors';
import {
  createManualBilling as createManualBillingApi,
  updateManualBilling as updateManualBillingApi,
  deleteManualBilling as deleteManualBillingApi,
} from 'api/subscription';

export interface ManualBilling {
  Id?: number;
  TeamId: number;
  UserId: number;
  Details: string;
  Amount: number;
  Card: string;
  BillingDate: string;
}

interface UseManualBillingProps {
  onSuccess?: () => void;
}

export const useManualBilling = ({ onSuccess }: UseManualBillingProps = {}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<ManualBilling | null>(null);

  const openModal = (billing?: ManualBilling) => {
    setSelectedBilling(billing || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedBilling(null);
    setIsModalOpen(false);
  };

  const createManualBilling = async (data: Omit<ManualBilling, 'Id'>) => {
    try {
      const response = await createManualBillingApi(data);
      showSuccessMessage('Manual billing created successfully');
      onSuccess?.();
      closeModal();
      return response?.data?.result;
    } catch (error) {
      showErrorMessage('Failed to create manual billing');
      throw error;
    }
  };

  const updateManualBilling = async (id: number, data: Partial<ManualBilling>) => {
    try {
      const response = await updateManualBillingApi({
        ...data,
        Id: id,
      });
      showSuccessMessage('Manual billing updated successfully');
      onSuccess?.();
      closeModal();
      return response.data.result;
    } catch (error) {
      showErrorMessage('Failed to update manual billing');
      throw error;
    }
  };

  const deleteManualBilling = async (id: number) => {
    try {
      await deleteManualBillingApi({ Id: id });
      showSuccessMessage('Manual billing deleted successfully');
      onSuccess?.();
    } catch (error) {
      showErrorMessage('Failed to delete manual billing');
      throw error;
    }
  };

  return {
    isModalOpen,
    selectedBilling,
    openModal,
    closeModal,
    createManualBilling,
    updateManualBilling,
    deleteManualBilling,
  };
};
